import type { Ref } from 'vue';
import { ref, computed, watch } from 'vue';
import { useHeaderThemeDetection } from './useHeaderThemeDetection';
import type {
  HeaderNavStyles,
  HeaderThemeState,
  MenuGridStyle,
  NavMenuGridInput,
} from '../types/headerTypes';

// --- Dropdowns: options & return type ---

export interface UseDesktopHeaderDropdownsOptions {
  /** Ref to the desktop nav element (for mouse-leave detection). */
  navRef?: Ref<HTMLElement | null>;
  /** Ref to the viewport element (pointer still over viewport = do not close nav on mouse leave). */
  viewportRef?: Ref<HTMLElement | null>;
}

export interface DesktopHeaderDropdowns {
  mainNavOpenValue: Ref<string | undefined>;
  autocompleteResultsOpen: Ref<boolean>;
  autocompleteInputActive: Ref<boolean>;
  languagePickerOpen: Ref<boolean>;
  isMenuOpen: Ref<boolean>;
  closeNavMenu: () => void;
  closeAll: () => void;
  onNavMouseLeave: (ev: MouseEvent) => void;
}

/**
 * Central state and actions for desktop header dropdowns (main nav, autocomplete, language picker).
 * Ensures only one of the three can be open at a time.
 */
export function useDesktopHeaderDropdowns(
  options: UseDesktopHeaderDropdownsOptions = {},
): DesktopHeaderDropdowns {
  const { navRef, viewportRef } = options;

  const mainNavOpenValue = ref<string | undefined>();
  const autocompleteResultsOpen = ref(false);
  const autocompleteInputActive = ref(false);
  const languagePickerOpen = ref(false);

  function closeOthersExcept(open: 'nav' | 'autocomplete' | 'language') {
    if (open !== 'nav') {
      mainNavOpenValue.value = undefined;
    }
    if (open !== 'autocomplete') {
      autocompleteResultsOpen.value = false;
    }
    if (open !== 'language') {
      languagePickerOpen.value = false;
    }
  }

  watch(mainNavOpenValue, (val) => {
    if (val != null) {
      closeOthersExcept('nav');
    }
  });
  watch(autocompleteResultsOpen, (open) => {
    if (open) {
      closeOthersExcept('autocomplete');
    }
  });
  watch(languagePickerOpen, (open) => {
    if (open) {
      closeOthersExcept('language');
    }
  });

  const isMenuOpen = computed(() => !!mainNavOpenValue.value);

  function closeNavMenu() {
    mainNavOpenValue.value = undefined;
  }

  function closeAll() {
    mainNavOpenValue.value = undefined;
    autocompleteResultsOpen.value = false;
    autocompleteInputActive.value = false;
    languagePickerOpen.value = false;
  }

  function onNavMouseLeave(ev: MouseEvent) {
    const movedTo = ev.relatedTarget as Node | null;
    if (movedTo != null && navRef?.value?.contains(movedTo)) {
      return;
    }
    if (viewportRef?.value && movedTo != null && viewportRef.value.contains(movedTo)) {
      return;
    }
    closeAll();
  }

  return {
    mainNavOpenValue,
    autocompleteResultsOpen,
    autocompleteInputActive,
    languagePickerOpen,
    isMenuOpen,
    closeNavMenu,
    closeAll,
    onNavMouseLeave,
  };
}

export interface UseDesktopHeaderViewportOptions {
  dropdowns: Pick<
    DesktopHeaderDropdowns,
    | 'isMenuOpen'
    | 'autocompleteResultsOpen'
    | 'languagePickerOpen'
    | 'autocompleteInputActive'
  >;
  mainNavInteractiveHovered: Ref<boolean>;
}

export interface DesktopHeaderViewport {
  isSurfaceOpen: Ref<boolean>;
  useOpenAppearance: Ref<boolean>;
  surfaceStyle: Ref<Record<string, string>>;
  barStyle: Ref<Record<string, string>>;
  themeState: Readonly<Ref<HeaderThemeState>>;
  headerStyles: Ref<HeaderNavStyles>;
  getMenuGridStyleFor: (menu: NavMenuGridInput | undefined) => MenuGridStyle;
}

/**
 * Viewport and surface/bar styles for the desktop header strip (unified layer + unroll).
 * Call with the result of useDesktopHeaderDropdowns.
 */
export function useDesktopHeaderViewport(
  options: UseDesktopHeaderViewportOptions,
): DesktopHeaderViewport {
  const { dropdowns, mainNavInteractiveHovered } = options;

  const isSurfaceOpen = computed(
    () =>
      dropdowns.isMenuOpen.value ||
      dropdowns.autocompleteResultsOpen.value ||
      dropdowns.languagePickerOpen.value,
  );

  const useOpenAppearance = computed(
    () =>
      isSurfaceOpen.value ||
      dropdowns.autocompleteInputActive.value ||
      mainNavInteractiveHovered.value,
  );

  const { themeState, headerStyles, getMenuGridStyleFor } = useHeaderThemeDetection({
    isAnyDropdownOpen: isSurfaceOpen,
  });

  const surfaceStyle = computed(() =>
    useOpenAppearance.value
      ? headerStyles.value.surfaceStyleOpenMenu
      : {
          backgroundColor: 'transparent',
          backdropFilter: 'none',
          webkitBackdropFilter: 'none',
        },
  );

  const barStyle = computed(() =>
    useOpenAppearance.value
      ? headerStyles.value.transparentStyleWhenOpen
      : headerStyles.value.desktopNavStyle,
  );

  return {
    isSurfaceOpen,
    useOpenAppearance,
    surfaceStyle,
    barStyle,
    themeState,
    headerStyles,
    getMenuGridStyleFor,
  };
}

// --- Unified facade ---

export interface UseDesktopHeaderOptions {
  /** Ref to the desktop nav element (for mouse-leave detection). */
  navRef?: Ref<HTMLElement | null>;
  /** Ref to the viewport element (pointer still over viewport = do not close nav on mouse leave). */
  viewportRef?: Ref<HTMLElement | null>;
}

export type DesktopHeader = DesktopHeaderDropdowns &
  DesktopHeaderViewport & {
    setAutocompleteHasResults: (value: boolean) => void;
    mainNavInteractiveHovered: Ref<boolean>;
    onNavMouseLeave: (ev: MouseEvent) => void;
  };

/**
 * Single entry point for desktop header: dropdowns, viewport styles, and event handlers.
 * Use this in PartialHeader instead of wiring useDesktopHeaderDropdowns + useDesktopHeaderViewport manually.
 */
export function useDesktopHeader(options: UseDesktopHeaderOptions = {}): DesktopHeader {
  const { navRef, viewportRef } = options;
  const autocompleteHasResults = ref(false);
  const mainNavInteractiveHovered = ref(false);

  const dropdowns = useDesktopHeaderDropdowns({ navRef, viewportRef });
  const viewport = useDesktopHeaderViewport({
    dropdowns,
    mainNavInteractiveHovered,
  });

  function setAutocompleteHasResults(value: boolean) {
    autocompleteHasResults.value = value;
  }

  function onNavMouseLeave(ev: MouseEvent) {
    dropdowns.onNavMouseLeave(ev);
  }

  return {
    ...dropdowns,
    ...viewport,
    setAutocompleteHasResults,
    mainNavInteractiveHovered,
    onNavMouseLeave,
  };
}
